'use client';

import {cn} from '../../lib/utils';

import type {ComponentProps} from 'react';

function Table({className, ...props}: ComponentProps<'table'>) {
  return (
    <div data-slot="table-container" className="relative w-full overflow-x-auto">
      <table
        data-slot="table"
        className={cn('w-full caption-bottom text-sm', className)}
        {...props}
      />
    </div>
  );
}

function TableHeader({className, ...props}: ComponentProps<'thead'>) {
  return <thead data-slot="table-header" className={cn('[&_tr]:border-b', className)} {...props} />;
}

function TableBody({className, ...props}: ComponentProps<'tbody'>) {
  return (
    <tbody
      data-slot="table-body"
      className={cn('[&_tr:last-child]:border-0', className)}
      {...props}
    />
  );
}

function TableFooter({className, ...props}: ComponentProps<'tfoot'>) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn(
        'border-t bg-background-secondary/50 font-medium [&>tr]:last:border-b-0',
        className
      )}
      {...props}
    />
  );
}

function TableRow({className, ...props}: ComponentProps<'tr'>) {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        'border-b transition-colors data-[state=selected]:bg-background-selected',
        className
      )}
      {...props}
    />
  );
}

function TableHead({className, ...props}: ComponentProps<'th'>) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        'h-11 px-6 py-3 text-start align-middle text-xs font-semibold text-fg-tertiary bg-background-secondary [&:has([role=checkbox])]:pe-0',
        className
      )}
      {...props}
    />
  );
}

function TableCell({className, ...props}: ComponentProps<'td'>) {
  return (
    <td
      data-slot="table-cell"
      className={cn(
        'px-6 py-4 align-middle overflow-hidden [&:has([role=checkbox])]:pe-0',
        className
      )}
      {...props}
    />
  );
}

function TableCaption({className, ...props}: ComponentProps<'caption'>) {
  return (
    <caption
      data-slot="table-caption"
      className={cn('mt-4 text-sm text-fg-tertiary', className)}
      {...props}
    />
  );
}

export {Table, TableBody, TableCaption, TableCell, TableFooter, TableHead, TableHeader, TableRow};
